import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Chip, LoadingState, TableLoadingSkeleton } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SignOut, CheckCircle, XCircle, Clock, MagnifyingGlass, House, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fileDownloadUrl } from "@/lib/files";

export default function VisitorLogs() {
    const { user } = useAuth();
    const [visitors, setVisitors] = useState([]);
    const [filteredVisitors, setFilteredVisitors] = useState([]);
    const [flats, setFlats] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [form, setForm] = useState({ name: "", phone: "", purpose: "", flat_id: "", vehicle_no: "" });

    async function load() {
        setLoading(true);
        try {
            const [vResponse, fResponse] = await Promise.all([api.get("/visitors"), api.get("/flats")]);
            // Sort by most recent first
            const sorted = vResponse.data.sort((a, b) => {
                const timeA = new Date(a.check_in || a.created_at || 0);
                const timeB = new Date(b.check_in || b.created_at || 0);
                return timeB - timeA;
            });
            setVisitors(sorted);
            setFilteredVisitors(sorted);
            setFlats(fResponse.data);
        } catch (e) {
            console.error("Failed to load visitors:", e);
            toast.error(formatError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
        // Auto-refresh every 30 seconds
        const interval = setInterval(load, 30000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        // Filter visitors based on search query (flat number)
        if (!searchQuery.trim()) {
            setFilteredVisitors(visitors);
            return;
        }

        const query = searchQuery.toLowerCase().trim();
        const filtered = visitors.filter(v => {
            if (!v.flat) return false;
            const flatNumber = `${v.flat.block}-${v.flat.number}`.toLowerCase();
            return flatNumber.includes(query) || 
                   v.flat.number.toString().includes(query) ||
                   v.flat.block.toLowerCase().includes(query);
        });
        setFilteredVisitors(filtered);
    }, [searchQuery, visitors]);

    async function checkout(visitor) {
        if (!confirm(`Check out ${visitor.name}?`)) return;
        try {
            await api.post(`/visitors/${visitor.id}/checkout`);
            toast.success(`${visitor.name} checked out successfully`);
            load();
        } catch (e) {
            toast.error(formatError(e));
        }
    }

    async function approveVisitor(visitor) {
        try {
            await api.post(`/visitors/${visitor.id}/approve`);
            toast.success(`${visitor.name} approved and checked in`);
            load();
        } catch (e) {
            toast.error(formatError(e));
        }
    }

    async function rejectVisitor(visitor) {
        if (!confirm(`Reject visitor ${visitor.name}?`)) return;
        try {
            await api.post(`/visitors/${visitor.id}/reject`);
            toast.success("Visitor entry rejected");
            load();
        } catch (e) {
            toast.error(formatError(e));
        }
    }

    async function submitLogVisitor(e) {
        e.preventDefault();
        try {
            const res = await api.post("/visitors", form);
            toast.success("Visitor logged");
            if (res.data.qr_code) {
                const link = document.createElement('a');
                link.href = res.data.qr_code;
                link.download = `visitor-qr-${res.data.id}.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                toast.success("QR code downloaded!");
            }
            setOpen(false);
            setForm({ name: "", phone: "", purpose: "", flat_id: "", vehicle_no: "" });
            load();
        } catch (e) { toast.error(formatError(e)); }
    }

    const formatTime = (iso) => {
        if (!iso) return "—";
        const date = new Date(iso);
        return date.toLocaleString([], {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    const getStatusChip = (visitor) => {
        if (visitor.status === "REQUESTED") {
            return <Chip color="yellow" icon={<Clock size={14} />}>Pending Approval</Chip>;
        }
        if (visitor.status === "REJECTED") {
            return <Chip color="red" icon={<XCircle size={14} />}>Rejected</Chip>;
        }
        if (visitor.check_out) {
            return <Chip color="gray">Checked Out</Chip>;
        }
        if (visitor.check_in) {
            return <Chip color="green" icon={<CheckCircle size={14} />}>Checked In</Chip>;
        }
        return <Chip color="blue">Registered</Chip>;
    };

    const pendingVisitors = filteredVisitors.filter(v => v.status === "REQUESTED");
    const activeVisitors = filteredVisitors.filter(v => 
        v.status !== "REQUESTED" && 
        v.status !== "REJECTED" && 
        !v.check_out
    );
    const completedVisitors = filteredVisitors.filter(v => v.check_out || v.status === "REJECTED");

    if (loading) {
        return (
            <div className="animate-fade-up">
                <PageHeader overline="Gate" title="Visitor Logs" description="Society-wide visitor activity — pending approvals, who's inside, and history." />
                <TableLoadingSkeleton rows={5} cols={7} />
            </div>
        );
    }

    return (
        <div className="animate-fade-up">
            <PageHeader
                overline="Gate"
                title="Visitor Logs"
                description="Society-wide visitor activity — pending approvals, who's inside, and history."
                actions={
                    <Button data-testid="log-visitor-btn" onClick={() => setOpen(true)} className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white">
                        <Plus size={16} className="mr-1.5" /> Log visitor
                    </Button>
                }
            />

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="log-visitor-dialog">
                    <DialogHeader><DialogTitle>Log a visitor</DialogTitle></DialogHeader>
                    <form onSubmit={submitLogVisitor} className="space-y-3">
                        <div className="space-y-1.5">
                            <Label className="text-xs">Visitor name *</Label>
                            <Input data-testid="log-visitor-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm border-brand-line" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label className="text-xs">Phone</Label>
                                <Input data-testid="log-visitor-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm border-brand-line" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Vehicle No.</Label>
                                <Input data-testid="log-visitor-vehicle-input" value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value.toUpperCase() })} className="rounded-sm border-brand-line" />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Purpose *</Label>
                            <Input data-testid="log-visitor-purpose-input" required value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="Delivery, guest, service..." className="rounded-sm border-brand-line" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Visiting flat *</Label>
                            <Select value={form.flat_id} onValueChange={(v) => setForm({ ...form, flat_id: v })}>
                                <SelectTrigger data-testid="log-visitor-flat-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Select flat" /></SelectTrigger>
                                <SelectContent>{flats.map((f) => <SelectItem key={f.id} value={f.id}>{f.block}-{f.number}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <DialogFooter>
                            <Button type="submit" disabled={!form.flat_id} data-testid="log-visitor-submit-btn" className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white">
                                Log visitor & generate pass
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Search Bar */}
            <div className="mb-6 bg-white border border-brand-line rounded-sm p-4">
                <div className="flex items-center gap-3">
                    <MagnifyingGlass size={20} className="text-brand-inkSoft" />
                    <Input
                        placeholder="Search by flat number (e.g., A-101, 101, A)"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="flex-1 border-brand-line rounded-sm"
                        />
                        {searchQuery && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSearchQuery("")}
                            >
                                Clear
                            </Button>
                        )}
                    </div>
                    <p className="text-xs text-brand-inkSoft mt-2">
                        Showing {filteredVisitors.length} of {visitors.length} visitors
                    </p>
                </div>

                {/* Pending Approvals Section */}
                {pendingVisitors.length > 0 && (
                    <div className="mb-6">
                        <h2 className="font-heading text-xl text-brand-ink mb-3 flex items-center gap-2">
                            <Clock size={24} className="text-yellow-600" />
                            Pending Approvals ({pendingVisitors.length})
                        </h2>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-sm overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-yellow-100">
                                        <tr className="text-left">
                                            <th className="p-3 text-xs font-medium text-brand-ink">Visitor</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Flat</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Purpose</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Vehicle</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Created</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pendingVisitors.map((v, i) => (
                                            <tr key={v.id} className={i % 2 === 0 ? "bg-white" : "bg-yellow-50"}>
                                                <td className="p-3">
                                                    <div className="flex items-center gap-2">
                                                        {v.photo_file_id && (
                                                            <img src={fileDownloadUrl(v.photo_file_id)} alt="" data-testid={`visitor-photo-${v.id}`} className="w-8 h-8 rounded-full object-cover border border-brand-line shrink-0" />
                                                        )}
                                                        <div>
                                                            <p className="text-sm font-medium text-brand-ink">{v.name}</p>
                                                            {v.phone && <p className="text-xs text-brand-inkSoft">{v.phone}</p>}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="p-3">
                                                    <div className="flex items-center gap-1 text-sm text-brand-ink">
                                                        <House size={14} className="text-brand-inkSoft" />
                                                        {v.flat ? `${v.flat.block}-${v.flat.number}` : "—"}
                                                    </div>
                                                </td>
                                                <td className="p-3 text-sm text-brand-inkSoft">{v.purpose || "—"}</td>
                                                <td className="p-3 text-sm text-brand-inkSoft">{v.vehicle_no || "—"}</td>
                                                <td className="p-3 text-xs text-brand-inkSoft">{formatTime(v.created_at)}</td>
                                                <td className="p-3">
                                                    <div className="flex gap-2">
                                                        <Button
                                                            size="sm"
                                                            onClick={() => approveVisitor(v)}
                                                            className="bg-green-600 hover:bg-green-700 text-white"
                                                        >
                                                            <CheckCircle size={14} className="mr-1" />
                                                            Approve
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => rejectVisitor(v)}
                                                            className="border-red-300 text-red-600 hover:bg-red-50"
                                                        >
                                                            <XCircle size={14} className="mr-1" />
                                                            Reject
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Active Visitors Section */}
                <div className="mb-6">
                    <h2 className="font-heading text-xl text-brand-ink mb-3 flex items-center gap-2">
                        <CheckCircle size={24} className="text-green-600" />
                        Active Visitors ({activeVisitors.length})
                    </h2>
                    {activeVisitors.length === 0 ? (
                        <div className="bg-white border border-brand-line rounded-sm p-8 text-center">
                            <p className="text-brand-inkSoft">No active visitors</p>
                        </div>
                    ) : (
                        <div className="bg-white border border-brand-line rounded-sm overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-brand-sage/30">
                                        <tr className="text-left">
                                            <th className="p-3 text-xs font-medium text-brand-ink">Visitor</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Flat</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Purpose</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Vehicle</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Check In</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Status</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activeVisitors.map((v, i) => (
                                            <tr key={v.id} className={i % 2 === 0 ? "bg-white" : "bg-brand-sage/10"}>
                                                <td className="p-3">
                                                    <div className="flex items-center gap-2">
                                                        {v.photo_file_id && (
                                                            <img src={fileDownloadUrl(v.photo_file_id)} alt="" data-testid={`visitor-photo-${v.id}`} className="w-8 h-8 rounded-full object-cover border border-brand-line shrink-0" />
                                                        )}
                                                        <div>
                                                            <p className="text-sm font-medium text-brand-ink">{v.name}</p>
                                                            {v.phone && <p className="text-xs text-brand-inkSoft">{v.phone}</p>}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="p-3">
                                                    <div className="flex items-center gap-1 text-sm text-brand-ink">
                                                        <House size={14} className="text-brand-inkSoft" />
                                                        {v.flat ? `${v.flat.block}-${v.flat.number}` : "—"}
                                                    </div>
                                                </td>
                                                <td className="p-3 text-sm text-brand-inkSoft">{v.purpose || "—"}</td>
                                                <td className="p-3 text-sm text-brand-inkSoft">{v.vehicle_no || "—"}</td>
                                                <td className="p-3 text-xs text-brand-inkSoft">{formatTime(v.check_in)}</td>
                                                <td className="p-3">{getStatusChip(v)}</td>
                                                <td className="p-3">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => checkout(v)}
                                                        className="border-brand-line hover:bg-brand-sage/20"
                                                    >
                                                        <SignOut size={14} className="mr-1" />
                                                        Check Out
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Completed Visitors Section */}
                <div>
                    <h2 className="font-heading text-xl text-brand-ink mb-3">
                        Completed ({completedVisitors.length})
                    </h2>
                    {completedVisitors.length === 0 ? (
                        <div className="bg-white border border-brand-line rounded-sm p-8 text-center">
                            <p className="text-brand-inkSoft">No completed visitors</p>
                        </div>
                    ) : (
                        <div className="bg-white border border-brand-line rounded-sm overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-100">
                                        <tr className="text-left">
                                            <th className="p-3 text-xs font-medium text-brand-ink">Visitor</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Flat</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Purpose</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Check In</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Check Out</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {completedVisitors.slice(0, 50).map((v, i) => (
                                            <tr key={v.id} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                                <td className="p-3">
                                                    <div>
                                                        <p className="text-sm font-medium text-brand-ink">{v.name}</p>
                                                        {v.phone && <p className="text-xs text-brand-inkSoft">{v.phone}</p>}
                                                    </div>
                                                </td>
                                                <td className="p-3">
                                                    <div className="flex items-center gap-1 text-sm text-brand-ink">
                                                        <House size={14} className="text-brand-inkSoft" />
                                                        {v.flat ? `${v.flat.block}-${v.flat.number}` : "—"}
                                                    </div>
                                                </td>
                                                <td className="p-3 text-sm text-brand-inkSoft">{v.purpose || "—"}</td>
                                                <td className="p-3 text-xs text-brand-inkSoft">{formatTime(v.check_in)}</td>
                                                <td className="p-3 text-xs text-brand-inkSoft">{formatTime(v.check_out)}</td>
                                                <td className="p-3">{getStatusChip(v)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {completedVisitors.length > 50 && (
                                <div className="p-3 bg-gray-50 border-t border-brand-line text-center text-xs text-brand-inkSoft">
                                    Showing 50 of {completedVisitors.length} completed visitors
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
    );
}
